import { Column, Index, JoinColumn, ManyToOne } from 'typeorm';
import { Entity } from '../../decorators';
import { BaseEntity } from './base.entity';
import { CampaignEntity } from './campaign.entity';

/**
 * One sending-provider campaign (in Instantly, essentially a single sequence)
 * belonging to a Workd campaign. A Workd campaign typically fans out to 5-20 of
 * these, and a contact is enrolled in exactly one (see CampaignMembershipEntity).
 *
 * Not auditable: rows are maintained by the provider sync (status / leadCount /
 * stats / lastSyncedAt), which would otherwise flood `_audit`.
 */
@Index('campaign_provider_campaign__external_uindex', ['externalCampaignId'], {
	unique: true,
})
@Index('campaign_provider_campaign__fk_campaign', ['fkCampaignId'], {})
@Entity('campaign_provider_campaign', {}, { auditable: false })
export class CampaignProviderCampaignEntity extends BaseEntity {
	@Column('int', { name: 'fk_campaign__id', unsigned: true })
	public fkCampaignId!: number;

	@Column('varchar', {
		name: 'provider',
		length: 50,
		default: () => "'instantly'",
	})
	public provider!: string;

	// The provider's campaign identifier (e.g. Instantly campaign id).
	@Column('varchar', { name: 'externalCampaignId', length: 255 })
	public externalCampaignId!: string;

	@Column('varchar', {
		name: 'externalCampaignName',
		length: 255,
		nullable: true,
	})
	public externalCampaignName!: string | null;

	@Column('varchar', { name: 'status', length: 50, nullable: true })
	public status!: string | null;

	@Column('int', { name: 'leadCount', nullable: true })
	public leadCount!: number | null;

	@Column('json', { name: 'stats', nullable: true })
	public stats!: Record<string, unknown> | null;

	@Column('datetime', { name: 'lastSyncedAt', nullable: true })
	public lastSyncedAt!: Date | null;

	/**
	 * PD-17613 — the release map of a Workd v2 campaign: one entry per email
	 * after the first, each an Instantly subsequence started by a shared
	 * "Workd: send email N" label. Null on v1 (Mission Control) rows.
	 */
	@Column('json', { name: 'subsequences', nullable: true })
	public subsequences!: Array<CampaignProviderSubsequenceModel> | null;

	@ManyToOne(() => CampaignEntity, {
		onDelete: 'CASCADE',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_campaign__id', referencedColumnName: 'id' }])
	public fkCampaign!: CampaignEntity;
}

/** One released email of a v2 campaign (campaign_provider_campaign.subsequences). */
export interface CampaignProviderSubsequenceModel {
	/** The campaign_step it sends. */
	stepNo: number;
	/** Its place among the campaign's emails (2 for the second email, ...). */
	emailNumber: number;
	/** Instantly subsequence id. */
	subsequenceId: string;
	/** The shared Instantly label that starts it ("Workd: send email N"). */
	labelName: string;
	labelId: string;
	/** The label's interest status value, which is what a lead's status is set to. */
	labelValue: number;
}
